/**
 * Paziresh24 design tokens — aligned with tailwind.config + Plasmic DS
 * primary #3861fb · brand #3F3F79 · secondary #00acac · gray #F8FAFB
 */
export const DOCTOR_HOME_PAGE_COLOR = '#F2F2F2';

export const ds = {
  radius: {
    card: 'rounded-2xl',
    tile: 'rounded-xl',
    inner: 'rounded-lg',
    pill: 'rounded-full',
  },
  shadow: {
    card: 'shadow-card',
    sm: 'shadow-sm',
    workstation: 'shadow-[0_8px_32px_rgba(23,23,23,0.06)]',
  },
  /** Unified icon containers — bg-primary/5 + text-primary everywhere */
  icon: {
    bg: 'bg-primary/5',
    ring: 'ring-1 ring-primary/10',
    color: 'text-primary',
    container: 'bg-primary/5 text-primary ring-1 ring-primary/10',
    containerTile: 'flex shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary',
    containerAction: 'flex shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary ring-1 ring-primary/10',
    containerCircle: 'flex shrink-0 items-center justify-center rounded-full bg-primary/5 text-primary',
  },
  progress: {
    track: 'bg-slate-100',
    fill: 'bg-primary',
    fillRating: 'bg-amber-500',
  },
  surface: {
    pageColor: DOCTOR_HOME_PAGE_COLOR,
    page: 'bg-[#F2F2F2]',
    card: 'bg-white border border-slate-100',
    muted: 'bg-slate-100',
    neutralSoft: 'bg-slate-50',
    primarySoft: 'bg-primary/5',
    /** @deprecated use ds.icon.bg — kept for backward compat */
    primaryTint: 'bg-primary/5',
    secondarySoft: 'bg-secondary/10',
    successSoft: 'bg-emerald-50',
    warningSoft: 'bg-amber-50',
    holidaySoft: 'bg-red-50',
    alertBanner: 'rounded-xl border border-amber-200 bg-amber-50',
    socialAccent: '!border-primary/15 !bg-primary/[0.03]',
    activeAccent: 'border-emerald-200/80',
    switcherTrack: 'border border-slate-200/80 bg-slate-100/80',
    switcherTabActive: 'bg-white/95 shadow-[0_1px_2px_rgba(23,23,23,0.06)]',
    iconCircle: 'bg-primary/5 text-primary',
    emptyState: 'rounded-xl bg-slate-50',
    feedCardSubtle: 'bg-white/70 backdrop-blur-sm',
    feedCardAccent: 'bg-gradient-to-br from-white to-slate-50/80',
    tile: 'bg-white',
  },
  workstation: {
    page: 'md:bg-[#e9eaee]',
    pageCanvas: 'bg-[#F2F2F2] md:bg-[#e9eaee]',
    widget: 'rounded-3xl border-0 bg-white shadow-[0_12px_40px_rgba(23,23,23,0.08)]',
    widgetSm: 'rounded-[1.375rem] border-0 bg-white shadow-[0_8px_28px_rgba(23,23,23,0.06)]',
  },
  layout: {
    pageShell: 'mx-auto w-full max-w-lg px-4 md:max-w-screen-xl md:px-6',
    pagePadding: 'pb-32 pt-1 md:pb-8 md:pt-2',
    workstationGrid: 'grid-cols-12 items-start gap-x-5 gap-y-4',
    workstationCalendar: 'col-span-12 flex min-w-0 flex-col gap-4 md:sticky md:top-20 md:col-span-3 md:self-start',
    workstationMain: 'col-span-12 flex min-w-0 flex-col gap-4 md:col-span-6',
    workstationTools: 'col-span-12 flex min-w-0 flex-col gap-4 md:sticky md:top-20 md:col-span-3 md:self-start',
    feedSectionGap: 'gap-3',
    headerStack: 'space-y-3',
    cardInset: 'px-3 py-3',
    rowPadding: 'px-3 py-2.5',
    rowPaddingWide: 'px-4 py-3.5',
    listRowGap: 'gap-3',
    carouselStrip: '-mx-4 flex gap-2.5 overflow-x-auto px-4 pb-1 scrollbar-hide snap-x snap-mandatory',
    carouselStripTight: '-mx-4 flex gap-2.5 overflow-x-auto px-4 pb-0.5 scrollbar-hide snap-x snap-mandatory',
    statsRowStrip: '-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 scrollbar-hide',
    feedCardBody: 'px-4 py-3',
    feedCardHeader: 'px-4 pb-1 pt-4',
    feedCardVisual: 'px-4 pt-3',
    sectionCardPadding: 'p-4',
    groupLabelPadding: 'px-3 py-1.5',
  },
  type: {
    section: 'text-sm font-bold text-slate-800',
    subsection: 'text-[13px] font-semibold tracking-tight text-slate-700',
    display: 'text-base font-bold text-slate-800',
    dateDay: 'text-xs font-medium text-slate-500',
    dateNum: 'text-xs font-bold',
    monthLabel: 'text-[8px] font-medium text-slate-400',
    tab: 'text-[11px] font-medium',
    tabActive: 'text-[11px] font-semibold text-slate-700',
    tabInactive: 'text-[11px] font-medium text-slate-400',
    metric: 'text-3xl font-bold text-slate-800',
    hero: 'text-5xl font-bold tabular-nums text-slate-900',
    cardTitle: 'text-sm font-bold text-slate-800',
    cardTitleSemibold: 'text-sm font-semibold leading-5 text-slate-800',
    cardBody: 'text-sm text-slate-600',
    body: 'text-sm text-slate-700',
    bodyMedium: 'text-sm font-medium leading-snug text-slate-800',
    caption: 'text-xs text-slate-500',
    captionSm: 'text-[10px] font-medium text-slate-500',
    captionMuted: 'text-xs text-slate-400',
    label: 'text-xs font-medium text-slate-500',
    link: 'text-xs font-medium text-primary',
    badge: 'text-xs font-semibold',
    count: 'text-base font-bold tabular-nums text-slate-900',
    insightValue: 'text-xl font-bold tabular-nums text-slate-900',
    insightValueLg: 'text-lg font-bold tabular-nums leading-tight text-slate-900',
    insightTitle: 'text-xs font-bold text-slate-800',
    pill: 'text-xs font-semibold text-primary',
    alert: 'text-xs font-medium text-red-500',
    success: 'font-semibold text-green-600',
    danger: 'text-red-500',
    online: 'text-primary',
    warningTitle: 'text-xs font-bold text-amber-900',
    warningBody: 'text-xs text-amber-800',
    emptyState: 'text-sm text-slate-500',
    button: 'text-xs font-bold',
    pillButton: 'rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-white',
  },
  timeline: {
    done: 'bg-secondary text-white',
    current: 'bg-primary text-white',
    upcoming: 'border-[1.5px] border-slate-200 bg-white',
    line: 'border-slate-200',
    lineActive: 'border-secondary/70',
    lineDone: 'bg-secondary',
    lineCurrent: 'bg-primary/35',
    lineUpcoming: 'bg-slate-200',
  },
  schedule: {
    nextPill: 'inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary',
  },
  motion: {
    staggerMs: 55,
    easeSnappy: 'cubic-bezier(0.32, 0.72, 0, 1)',
    enter: 'animate-doctor-fade-up motion-reduce:animate-none opacity-0 motion-reduce:opacity-100',
    contentSwap: 'animate-doctor-content-in motion-reduce:animate-none',
    contentFade: 'animate-doctor-content-fade motion-reduce:animate-none',
    contentSlide: 'animate-doctor-content-slide motion-reduce:animate-none',
    pop: 'animate-doctor-pop motion-reduce:animate-none',
    walletSwap: 'animate-doctor-wallet-swap motion-reduce:animate-none',
    sheetEnter: 'animate-doctor-sheet-in motion-reduce:animate-none opacity-0 motion-reduce:opacity-100',
    sheetExit: 'animate-doctor-sheet-out motion-reduce:animate-none',
    originEnter: 'animate-doctor-origin-in motion-reduce:animate-none opacity-0 motion-reduce:opacity-100',
    originExit: 'animate-doctor-origin-out motion-reduce:animate-none',
    backdropEnter: 'animate-doctor-backdrop-in motion-reduce:animate-none',
    backdropExit: 'animate-doctor-backdrop-out motion-reduce:animate-none',
    collapse: 'transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none',
    pillSlide: 'transition-[inset-inline-start,width] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none',
    chevron: 'transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none',
    press: 'transition-transform duration-150 ease-out active:scale-[0.97]',
    hoverLift: 'transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-px hover:shadow-md',
    surface: 'transition-[transform,box-shadow,background-color,border-color,opacity] duration-200 ease-out',
    color: 'transition-colors duration-200 ease-out',
    listRow: 'transition-[transform,background-color,opacity] duration-200 ease-out active:scale-[0.985] hover:bg-slate-50/90',
  },
} as const;
